/**
 * The opening and offer editor on Settings -> Phone -> AI calls out: its draft,
 * its patch and the server's preview of the openings the texts make.
 *
 * The preview is the SERVER's (`POST /ai-outbound/opening-preview`), not a copy
 * of the opening builder in the browser: the company name is trimmed of its
 * legal form, the domain of "www.", and the check that gates a call runs on the
 * result. A second builder here would drift from that one silently, and the
 * owner would approve an opening no call ever says.
 *
 * React-free: `aiOutboundOpening.test.ts` runs it under plain node.
 */
import { api } from "./api";
import {
  OPENING_REASON_SLOTS,
  OUTBOUND_GOALS,
  OUTBOUND_TEXT_LIMITS,
  type AiOutboundSettings,
  type AiOutboundSettingsPatch,
  type OpeningReasonSlot,
  type OutboundGoal,
} from "./aiOutbound";

/** What a failed opening check names. Mirrors `OPENING_PROBLEMS` (`lib/outbound/opening.ts`). */
export const OPENING_PROBLEMS = [
  "no_ai_disclosure",
  "no_recording_notice",
  "no_refusal_line",
  "no_source_line",
  "no_question_end",
  "unfilled_placeholder",
] as const;

/**
 * The placeholders a reason sentence may use. `{viga}` is the company's most
 * important finding from the development brain, as one spoken sentence; a call
 * only says a sentence with it to a company that has one.
 */
export const OPENING_PLACEHOLDERS = ["{firma}", "{domeen}", "{viga}"] as const;

/** The placeholder that needs a finding (`FINDING_PLACEHOLDER` in `opening.ts`). */
export const FINDING_PLACEHOLDER = "{viga}";

/** One opening the texts make, as the server built it. */
export interface OpeningVariant {
  slot: OpeningReasonSlot;
  /** 0 is the slot's first sentence, 1 and up its extra ones; absent from an older server (read as 0). */
  index?: number;
  /** The sentence names the finding; absent from an older server. */
  needsFinding?: boolean;
  builtIn: boolean;
  reasonTemplate: string;
  text: string;
  mobileText: string;
  problems: string[];
  words: number;
  mobileWords: number;
  seconds: number;
  mobileSeconds: number;
}

export interface OpeningPreview {
  variants: OpeningVariant[];
  problems: { field: string; problems: string[] }[];
  /** Past this many seconds the page says the opening is long. */
  longSeconds: number;
}

export function previewOpening(body: {
  openingReasons: Record<OpeningReasonSlot, string>;
  extraOpeningReasons?: Record<OpeningReasonSlot, string[]>;
  openingQuestion: string;
}): Promise<OpeningPreview> {
  return api.post("/admin/crm/calls/ai-outbound/opening-preview", body);
}

// ---------------------------------------------------------------- draft

export interface OpeningDraft {
  reasons: Record<OpeningReasonSlot, string>;
  /**
   * The extra sentences per slot, as typed (blank entries allowed while
   * typing). Absent in a campaign's draft: a campaign words one sentence per slot.
   */
  extras?: Record<OpeningReasonSlot, string[]>;
  question: string;
  pitch: string;
  goal: OutboundGoal;
}

/** No extra sentences in any slot. */
export function blankExtras(): Record<OpeningReasonSlot, string[]> {
  return { letter: [], call: [], cold: [] };
}

export function toOpeningDraft(s: AiOutboundSettings): OpeningDraft {
  return {
    reasons: { letter: s.openingReasons?.letter ?? "", call: s.openingReasons?.call ?? "", cold: s.openingReasons?.cold ?? "" },
    extras: {
      letter: [...(s.extraOpeningReasons?.letter ?? [])],
      call: [...(s.extraOpeningReasons?.call ?? [])],
      cold: [...(s.extraOpeningReasons?.cold ?? [])],
    },
    question: s.openingQuestion ?? "",
    pitch: s.pitch ?? "",
    goal: (OUTBOUND_GOALS as readonly string[]).includes(s.goal) ? s.goal : "meeting",
  };
}

/** One spoken line as the server keeps it: whitespace folded, capped. */
export function spokenLine(text: string, max: number): string {
  return text.replace(/\s+/g, " ").trim().slice(0, max).trim();
}

/** The offer as the server keeps it: lines trimmed, no runs of blank lines, capped. */
export function pitchText(text: string): string {
  return text
    .split(/\r?\n/)
    .map((line) => line.replace(/[ \t]+/g, " ").trim())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim()
    .slice(0, OUTBOUND_TEXT_LIMITS.pitch)
    .trim();
}

/**
 * The editor's part of the settings patch: only what differs from what the
 * server last said, each reason slot on its own, so a save never resets a
 * text another tab changed in the meantime.
 */
export function toOpeningPatch(
  d: OpeningDraft,
  saved: AiOutboundSettings,
): Pick<AiOutboundSettingsPatch, "openingReasons" | "extraOpeningReasons" | "openingQuestion" | "pitch" | "goal"> {
  const patch: Pick<AiOutboundSettingsPatch, "openingReasons" | "extraOpeningReasons" | "openingQuestion" | "pitch" | "goal"> = {};
  const reasons: Partial<Record<OpeningReasonSlot, string>> = {};
  const extras: Partial<Record<OpeningReasonSlot, string[]>> = {};
  for (const slot of OPENING_REASON_SLOTS) {
    const value = spokenLine(d.reasons[slot], OUTBOUND_TEXT_LIMITS.reason);
    if (value !== (saved.openingReasons?.[slot] ?? "")) reasons[slot] = value;
    if (d.extras) {
      const list = extraSentences(d.extras[slot]);
      if (JSON.stringify(list) !== JSON.stringify(saved.extraOpeningReasons?.[slot] ?? [])) extras[slot] = list;
    }
  }
  if (Object.keys(reasons).length > 0) patch.openingReasons = reasons;
  if (Object.keys(extras).length > 0) patch.extraOpeningReasons = extras;
  const question = spokenLine(d.question, OUTBOUND_TEXT_LIMITS.question);
  if (question !== (saved.openingQuestion ?? "")) patch.openingQuestion = question;
  const pitch = pitchText(d.pitch);
  if (pitch !== (saved.pitch ?? "")) patch.pitch = pitch;
  if (d.goal !== saved.goal) patch.goal = d.goal;
  return patch;
}

/**
 * A slot's extra sentences as the server keeps them: each one line, blanks and
 * repeats dropped, at most `extraReasons`.
 */
export function extraSentences(list: readonly string[] | undefined): string[] {
  const kept = (list ?? []).map((text) => spokenLine(text, OUTBOUND_TEXT_LIMITS.reason)).filter(Boolean);
  return [...new Set(kept)].slice(0, OUTBOUND_TEXT_LIMITS.extraReasons);
}

/**
 * Where the extra sentence typed at `i` sits in the list the server keeps
 * (`extraSentences`), or -1 when the server drops it (blank, or a repeat of an
 * earlier one). The preview and the save name an extra by that position.
 */
export function extraServerIndex(list: readonly string[], i: number): number {
  const own = spokenLine(list[i] ?? "", OUTBOUND_TEXT_LIMITS.reason);
  if (!own) return -1;
  const before = extraSentences(list.slice(0, i));
  if (before.includes(own)) return -1;
  return before.length < OUTBOUND_TEXT_LIMITS.extraReasons ? before.length : -1;
}

/** The part of the draft the preview depends on, as one comparable string. */
export function openingPreviewKey(d: Pick<OpeningDraft, "reasons" | "question" | "extras">): string {
  return JSON.stringify([d.reasons.letter, d.reasons.call, d.reasons.cold, d.question, d.extras ?? null]);
}

/** A problem code as a catalogue suffix: the code itself, or "unknown". */
export function openingProblemKey(code: string): string {
  return (OPENING_PROBLEMS as readonly string[]).includes(code) ? code : "unknown";
}

/** The problems the server found for one editor field ("openingReasons.cold", "openingQuestion"). */
export function problemsFor(preview: OpeningPreview | null, field: string): string[] {
  return preview?.problems.find((p) => p.field === field)?.problems ?? [];
}

/** A text with a placeholder put in at the end, with one space before it when needed. */
export function withPlaceholder(text: string, placeholder: string): string {
  if (!text) return placeholder;
  return /\s$/.test(text) ? `${text}${placeholder}` : `${text} ${placeholder}`;
}
